/**
 * Academy Image Marquee
 * ---------------------------------------------------------------------------
 * marquee : klont die Bildergruppe so oft, bis die Track mindestens die
 *           doppelte Viewport-Breite hat → der reine CSS-Loop läuft nahtlos,
 *           egal wie wenige Blöcke gepflegt sind. Keine Reveal-Animation.
 * static  : GSAP-Reveal – jedes Bild skaliert von 0 → 1 und slidet mit
 *           Stagger und weicher Ease nach oben. Ohne GSAP bleiben die Bilder
 *           schlicht sichtbar (Progressive Enhancement).
 */
(function () {
  'use strict';

  // Animation nur laufen lassen, während die Marquee (fast) im Viewport ist.
  // Eine `linear infinite` Transform-Animation hält den Compositor sonst die
  // ganze Seitenlebensdauer bei jedem Frame beschäftigt und kann so z. B. auf
  // der Produktseite Scroll-Ruckeln verursachen.
  function pauseWhenOffscreen(root) {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          root.classList.toggle('is-paused', !entry.isIntersecting);
        });
      },
      { rootMargin: '200px 0px' } // etwas vor dem Sichtbarwerden starten
    );
    io.observe(root);
  }

  function initMarquee(root) {
    var track = root.querySelector('[data-marquee-track]');
    var viewport = root.querySelector('[data-marquee-viewport]');
    var group = root.querySelector('[data-marquee-group]');
    if (!track || !viewport || !group) return;

    pauseWhenOffscreen(root);

    // Liquid rendert die Bildergruppe bereits zweimal. Reicht die Breite
    // nicht für einen nahtlosen Loop (mind. 2x Viewport), einmalig die
    // fehlende Anzahl Kopien berechnen und anhängen – ohne Mess-Schleife,
    // damit kein Layout-Thrashing und keine Endlos-Kacheln entstehen.
    var twoCopies = track.scrollWidth;
    var need = viewport.offsetWidth * 2;
    if (!twoCopies || twoCopies >= need) return;

    var oneCopy = twoCopies / 2;
    // Klon-Obergrenze bewusst niedrig (6): begrenzt Anzahl <img> in der einen
    // großen Compositing-Ebene.
    var copiesToAdd = Math.min(Math.ceil(need / oneCopy) - 2, 6);
    for (var i = 0; i < copiesToAdd; i++) {
      var clone = group.cloneNode(true);
      clone.removeAttribute('data-marquee-group');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    // Loop-Distanz = exakt eine Gruppenbreite inkl. des trennenden gap.
    var styles = getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    track.style.setProperty('--aim-shift', '-' + (oneCopy + gap / 2) + 'px');
  }

  function initStatic(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('.academy-image-marquee__item'));
    if (!items.length) return;

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var ease = (typeof CustomEase !== 'undefined' && CustomEase.get('osmo-ease'))
      ? 'osmo-ease'
      : 'power3.out';

    gsap.set(items, { autoAlpha: 0, scale: 0, y: 40, transformOrigin: '50% 100%' });

    ScrollTrigger.batch(items, {
      start: 'top 85%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          autoAlpha: 1,
          scale: 1,
          y: 0,
          duration: 1,
          ease: ease,
          stagger: 0.12,
        });
      },
    });
  }

  // Adds `.is-revealed` to the marquee root once it scrolls into view — drives
  // the %%…%% heading font-swap keyframe (the ||…|| pill marks run off their
  // own observer in academy-pill-mark.js).
  function revealOnView(root) {
    if (root.classList.contains('is-revealed')) return;
    if (!('IntersectionObserver' in window)) {
      root.classList.add('is-revealed');
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(root);
  }

  // Freeze running marquees while the user is actively scrolling. A
  // full-viewport-width `linear infinite` transform keeps the compositor busy
  // every frame; pausing it for the ~180 ms around a scroll gesture hands
  // those frames back to the scroll itself (matters most alongside the fixed
  // sticky add-to-cart layer). Resumes shortly after scrolling stops.
  var scrollRoots = [];
  var scrollIdle = null;
  function onScrollActivity() {
    for (var i = 0; i < scrollRoots.length; i++) {
      scrollRoots[i].classList.add('is-user-scrolling');
    }
    if (scrollIdle) clearTimeout(scrollIdle);
    scrollIdle = setTimeout(function () {
      for (var i = 0; i < scrollRoots.length; i++) {
        scrollRoots[i].classList.remove('is-user-scrolling');
      }
    }, 180);
  }

  function init() {
    var roots = document.querySelectorAll('[data-academy-image-marquee]:not([data-aim-init])');
    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-aim-init', '');
      revealOnView(root);
      if (root.dataset.mode === 'marquee') {
        if (!scrollRoots.length) {
          window.addEventListener('scroll', onScrollActivity, { passive: true });
        }
        scrollRoots.push(root);
        initMarquee(root);
      } else {
        initStatic(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-Editor: neu gerenderte Section erneut initialisieren.
  document.addEventListener('shopify:section:load', init);
})();
